// utils/excelExport.js
import * as moment from "moment-timezone";
import ExcelJS from "exceljs";

export const exportToExcel = async (
  query,
  transactions2,
  DataCSVFilters,
  DataCSV,
  totalByStatus,
  timezone = "UTC"
) => {
  try {
    // Создание workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Transactions Report");

    // Get timezone label for display
    const getTimezoneLabel = (tz) => {
      // You might want to import timezoneOptions here or pass it as parameter
      // For now, we'll use a simple display format
      return tz === "UTC" ? "UTC" : tz;
    };

    // ===== РАЗДЕЛ 1: ПАРАМЕТРЫ ВЫБОРКИ =====
    // Заголовок "Параметры выборки"
    const filterTitleRow = worksheet.getRow(1);
    filterTitleRow.getCell(1).value = "Параметры выборки";
    worksheet.mergeCells("A1:I1"); // Extended to include timezone

    // Стиль для заголовка параметров
    filterTitleRow.getCell(1).style = {
      font: { bold: true, size: 12, color: { argb: "FFFFFF" } },
      fill: { type: "pattern", pattern: "solid", fgColor: { argb: "4472C4" } },
      alignment: { horizontal: "center", vertical: "middle" },
      border: {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      },
    };

    const filterHeaders = [
      "req_acc",
      "req_id / pay_uno",
      "command",
      "currency",
      "status",
      "minAmount",
      "maxAmount",
      "dateFrom",
      "dateTo",
    ];

    const filterHeaderRow = worksheet.getRow(2);
    filterHeaders.forEach((header, index) => {
      const cell = filterHeaderRow.getCell(index + 1);
      cell.value = header;
      cell.style = {
        font: { bold: true, color: { argb: "000000" } },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "D3D3D3" } },
        alignment: { horizontal: "center", vertical: "middle" },
        border: {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        },
      };
    });

    // Данные фильтров (добавили timezone)
    const filterData = DataCSVFilters[0];
    const filterValues = [
      filterData.req_acc || "",
      filterData.req_id || "",
      filterData.command || "",
      filterData.currency || "",
      filterData.status || "",
      filterData.minAmount || "",
      filterData.maxAmount || "",
      filterData.dateFrom || "",
      filterData.dateTo || "",
    ];

    const filterDataRow = worksheet.getRow(3);
    filterValues.forEach((value, index) => {
      const cell = filterDataRow.getCell(index + 1);
      cell.value = value;
      cell.style = {
        alignment: { horizontal: "center", vertical: "middle" },
        border: {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        },
      };
    });

    // ===== РАЗДЕЛ 2: ТАБЛИЦА ТРАНЗАКЦИЙ =====
    const transactionStartRow = 6;

    // Заголовок "Таблица транзакций"
    const transactionTitleRow = worksheet.getRow(transactionStartRow);
    transactionTitleRow.getCell(1).value = "Таблица транзакций";
    worksheet.mergeCells(`A${transactionStartRow}:I${transactionStartRow}`); // Extended for new columns

    transactionTitleRow.getCell(1).style = {
      font: { bold: true, size: 12, color: { argb: "FFFFFF" } },
      fill: { type: "pattern", pattern: "solid", fgColor: { argb: "4472C4" } },
      alignment: { horizontal: "center", vertical: "middle" },
      border: {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      },
    };

    // Заголовки таблицы транзакций (обновили согласно новой структуре)
    const transactionHeaders = [
      "uuid",
      "status",
      "pay_p",
      "rec_ssn",
      "amount",
      "currency",
      "createdAt",
      "updatedAt",
      "timezone",
    ];
    const transactionHeaderRow = worksheet.getRow(transactionStartRow + 1);

    transactionHeaders.forEach((header, index) => {
      const cell = transactionHeaderRow.getCell(index + 1);
      cell.value = header;
      cell.style = {
        font: { bold: true, color: { argb: "000000" } },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "D3D3D3" } },
        alignment: { horizontal: "center", vertical: "middle" },
        border: {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        },
      };
    });

    // Данные транзакций
    DataCSV.forEach((transaction, rowIndex) => {
      const actualRowNumber = transactionStartRow + 2 + rowIndex;
      const row = worksheet.getRow(actualRowNumber);

      // Разделяем amount и currency
      const amountString = transaction.amount || "";
      const amountMatch = amountString.match(/^([\d,.]+)\s*(.*)$/);
      const amount = amountMatch ? amountMatch[1] : "";
      const currency = amountMatch ? amountMatch[2].trim() : "";

      // The dates are already formatted in the DataCSV from the main component
      // We just use them as is since they're already in the correct timezone
      const createdAt = transaction.createdAt || "";
      const updatedAt = transaction.updatedAt || "";
      const timezoneDisplay = transaction.timezone || getTimezoneLabel(timezone);

      const values = [
        transaction.uuid,
        transaction.status,
        transaction.pay_p,
        transaction.rec_ssn,
        amount,
        currency,
        createdAt,
        updatedAt,
        timezoneDisplay,
      ];

      // Определяем цвет фона в зависимости от статуса
      let fillColor = "FFFFFF"; // белый по умолчанию
      if (transaction.status === "completed") {
        fillColor = "C6EFCE"; // светло-зеленый
      } else if (transaction.status === "failed") {
        fillColor = "FFC7CE"; // светло-красный
      } else if (transaction.status === "pending" || transaction.status === "nmtCheck") {
        fillColor = "FFEB9C"; // светло-желтый
      }

      values.forEach((value, colIndex) => {
        const cell = row.getCell(colIndex + 1);
        cell.value = value;
        cell.style = {
          fill: { type: "pattern", pattern: "solid", fgColor: { argb: fillColor } },
          alignment: { horizontal: "center", vertical: "middle" },
          border: {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          },
        };
      });
    });

    // ===== РАЗДЕЛ 3: ИТОГИ ПО СТАТУСАМ =====
    const summaryStartRow = transactionStartRow + 2 + DataCSV.length + 2;

    // Заголовок итогов
    const summaryTitleRow = worksheet.getRow(summaryStartRow);
    summaryTitleRow.getCell(1).value = "Итоги по статусам";
    worksheet.mergeCells(`A${summaryStartRow}:C${summaryStartRow}`);

    summaryTitleRow.getCell(1).style = {
      font: { bold: true, size: 12, color: { argb: "FFFFFF" } },
      fill: { type: "pattern", pattern: "solid", fgColor: { argb: "E74C3C" } },
      alignment: { horizontal: "center", vertical: "middle" },
      border: {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      },
    };

    // Заголовки таблицы итогов
    const summaryHeaders = ["Status", "Total Amount", "Currency"];
    const summaryHeaderRow = worksheet.getRow(summaryStartRow + 1);

    summaryHeaders.forEach((header, index) => {
      const cell = summaryHeaderRow.getCell(index + 1);
      cell.value = header;
      cell.style = {
        font: { bold: true, color: { argb: "000000" } },
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "D3D3D3" } },
        alignment: { horizontal: "center", vertical: "middle" },
        border: {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        },
      };
    });

    // Добавляем итоги по статусам
    let summaryRowIndex = 0;
    Object.entries(totalByStatus).forEach(([status, total]) => {
      const actualRowNumber = summaryStartRow + 2 + summaryRowIndex;
      const row = worksheet.getRow(actualRowNumber);

      // Определяем валюту
      const sampleTransaction = DataCSV.find((item) => item.status === status);
      const currencyFromTransaction = sampleTransaction
        ? sampleTransaction.amount.match(/^[\d,.]+\s*(.*)$/)?.[1]?.trim() || "RUB"
        : "RUB";

      const summaryValues = [status, total.toFixed(2), currencyFromTransaction];

      summaryValues.forEach((value, colIndex) => {
        const cell = row.getCell(colIndex + 1);
        cell.value = value;
        cell.style = {
          alignment: { horizontal: colIndex === 1 ? "right" : "center", vertical: "middle" },
          numFmt: colIndex === 1 ? "#,##0.00" : undefined,
          border: {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          },
        };
      });
      summaryRowIndex++;
    });

    // Устанавливаем ширину колонок (обновили для новых колонок)
    worksheet.columns = [
      { width: 35 }, // uuid
      { width: 15 }, // status
      { width: 28 }, // pay_p
      { width: 28 }, // rec_ssn
      { width: 15 }, // amount
      { width: 12 }, // currency
      { width: 20 }, // createdAt
      { width: 20 }, // updatedAt
      { width: 20 }, // timezone
    ];

    // Генерируем имя файла с текущей датой в указанной timezone
    const timestamp = moment().tz(timezone).format("YYYY-MM-DD_HH-mm-ss");
    const filename = `Fora_to_EasyPay_Transactions_${timestamp}.xlsx`;

    // Экспортируем файл
    const buffer = await workbook.xlsx.writeBuffer();

    // Создаем blob и скачиваем файл
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    return {
      success: true,
      filename: filename,
      recordsCount: DataCSV.length,
      statusSummary: totalByStatus,
      timezone: getTimezoneLabel(timezone),
    };
  } catch (error) {
    console.error("Excel export error:", error);
    return {
      success: false,
      error: error.message,
    };
  }
};

function formatNumberWithCommas(number) {
  // Convert the number to a string
  let numberStr = number.toString();

  // Split the string into integer and decimal parts
  let parts = numberStr.split(".");

  // Extract the integer part and add commas for thousands separators
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Rejoin the integer and decimal parts with a dot (.) and return the result
  return parts.join(".");
}

export { formatNumberWithCommas };
